import {
	Card,
	CardContent,
	Typography,
	Stack,
	Divider,
	Icon,
	InputAdornment,
	Grid,
	Alert,
	styled,
	TextField,
} from '@mui/material';

import { useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { deleteRelatedEntity } from 'common/api';
import { ReactComponent as CloseIcon } from 'common/assets/images/close.svg';
import { ReactComponent as DeleteIcon } from 'common/assets/images/delete-icon.svg';
import { ReactComponent as Plus } from 'common/assets/images/Plus.svg';
import { ReactComponent as SearchOutlined } from 'common/assets/images/search-outline.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import ModalEle from 'common/components/Modal/ModalEle';
import InfoTooltip from 'common/components/Tooltip/Tooltip';
import { MODULES } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';

import { ICoveredProductItems } from '../api/type-def';

import { infoIcon } from '../../../../common/assets/images';
import {
	ENTITY_TYPE,
	ADD_PRODUCT_LABEL,
	NO_ITEMS_TO_DISPLAY,
} from '../../../../common/constants/TaxonomyConstants';
import {
	scrollProps,
	toSentenceCase,
} from '../../../../common/utils/utilFunctions';

import AddCoveredProduct from './AddNewCover/AddCoveredProduct';

import styles from './Overview.module.scss';

const { backgroundColor, borderColor } = styles;

const { OK } = HTTP_STATUS_CODES;

const { RISK_REFERENCE_TAXONOMY } = MODULES;

const StyledTextField = styled(TextField)(() => ({
	background: backgroundColor,
	borderRadius: '4px',
	border: `1px solid ${borderColor}`,
	width: '60%',
}));
interface ICoveredProps {
	heading: string;
	coveredLists: ICoveredProductItems[];
	tooltip: string;
	enableSearch?: boolean;
	id?: string;
	productDetails?: any;
	onHoverDelete?: boolean;
	onAdd?: () => void;
	addNew?: boolean;
	onDelete?: () => void;
	entityType?: string;
	entityId: string;
}

const CoveredComp = (props: ICoveredProps) => {
	const {
		heading,
		coveredLists = [],
		tooltip,
		enableSearch,
		id,
		productDetails = [],
		onHoverDelete,
		onAdd,
		addNew,
		onDelete,
		entityType = '',
		entityId,
	} = props;

	const [searchQuery, setSearchQuery] = useState<string>('');
	const [showDeletionModal, setShowDeletionModal] = useState<boolean>(false);
	const [selectedLabel, setSelectedLabel] = useState<string>('');
	const [selectedProductId, setSelectedProductId] = useState<string>('');
	const [openAddModal, setOpenAddModal] = useState<boolean>(false);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const dispatch = useDispatch();
	const { CITATION, REGULATION } = ENTITY_TYPE;

	const {
		deleteIconFillColor,
		statusWarningColor,
		statusRed,
		drawerCloseIconColor,
		selectedTabText,
	} = styles;

	let filteredLists: ICoveredProductItems[] = [];
	if (coveredLists) {
		filteredLists = coveredLists
			.filter((item) => item.productName !== null)
			.filter((label) =>
				label.productName.toLowerCase().includes(searchQuery.toLowerCase()),
			);
	}

	const handleDeleteIconClick = (label: string, productId: string) => {
		setShowDeletionModal(true);
		setSelectedLabel(label);
		setSelectedProductId(productId);
	};
	const handleModalCancelClick = () => {
		setShowDeletionModal(false);
	};
	const deletionMessage = `Are you sure you want to remove "${selectedLabel}"?`;

	const handleRemoveItem = async () => {
		const response = await deleteRelatedEntity(
			selectedProductId,
			id || '',
			RISK_REFERENCE_TAXONOMY,
			loggedInUser?.email || '',
			true,
		);
		if (response.statusCode === OK) {
			dispatch(
				setToastMessage({
					message: 'Product Deleted Successfully.',
					severity: 'success',
					isToastOpen: true,
				}),
			);
			setShowDeletionModal(false);
			if (onDelete) {
				onDelete();
			}
		} else {
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
		if (onAdd) {
			onAdd();
		}
	};

	const handleAddNew = () => {
		setOpenAddModal(true);
	};

	const closeEditModal = useCallback(() => {
		setOpenAddModal(false);
	}, []);

	return (
		<Card
			variant="outlined"
			className={
				filteredLists.length > 0
					? styles['covered-card-style']
					: styles['covered-card-noitemsstyle']
			}
			data-testid="covered-products"
		>
			<CardContent>
				<Stack direction="row" spacing={1} className={styles['header-wrapper']}>
					<div className={styles['header-title-wrapper']}>
						<Typography className={styles['covered-header']} gutterBottom>
							{toSentenceCase(heading)}&nbsp;{`(${filteredLists.length})`}
						</Typography>
						<div>
							<InfoTooltip
								className={styles['overview-info-tooltip']}
								title={tooltip}
							>
								<img
									src={infoIcon}
									alt="info"
									className={styles['overview-info']}
								/>
							</InfoTooltip>
						</div>
					</div>
				</Stack>
				<Grid container spacing={2} justifyContent="space-between">
					<Grid item xs={10}>
						{enableSearch && (
							<StyledTextField
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchOutlined className={styles['input-search-icon']} />
										</InputAdornment>
									),
									inputProps: { 'data-testid': 'txt-policy-search' },
								}}
								placeholder="Search"
								size="small"
								variant="outlined"
								className={styles['products-search']}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						)}
					</Grid>
					<Grid item xs={2} className={styles['btn-container']}>
						{addNew ? (
							<ButtonComp
								btnType="secondary"
								startIcon={<Plus fill={selectedTabText} />}
								onClick={handleAddNew}
								data-testid="add-mapping-btn"
								size="small"
								compClass={styles['products-btn']}
							>
								{toSentenceCase(ADD_PRODUCT_LABEL)}
							</ButtonComp>
						) : null}
					</Grid>
				</Grid>
				{filteredLists.length > 0 ? (
					<Stack
						className={
							entityType === CITATION || entityType === REGULATION
								? styles['covered-data']
								: styles['only-covered-data']
						}
						sx={scrollProps()}
					>
						{filteredLists.map((label: ICoveredProductItems, index: number) => (
							<div
								key={label.productId}
								className={`${onHoverDelete ? styles['row-item'] : ''}`}
							>
								<div className={styles['row-content']}>
									<Typography className={styles['link-style']}>
										{label.productName}
									</Typography>
									<div
										className={`${styles['delete-icon-container']} ${
											label.productAuthor &&
											loggedInUser?.userId !== label.productAuthor
												? styles['disable-icon']
												: styles['show-delete-icon']
										}`}
									>
										<Icon
											className={styles['workflow-list-item-delete']}
											data-testid="delete-action"
											onClick={() =>
												handleDeleteIconClick(
													label.productName,
													productDetails[index]?.productId,
												)
											}
										>
											<DeleteIcon
												data-testid="delete-icon"
												className={styles['status-delete-stack']}
												fill={deleteIconFillColor}
											/>
										</Icon>
									</div>
								</div>
								<Divider className={styles['divider-width']} />
							</div>
						))}
					</Stack>
				) : (
					<Stack
						className={
							entityType === ENTITY_TYPE.CITATION ||
							entityType === ENTITY_TYPE.REGULATION
								? styles['covered-data']
								: styles['only-covered-data']
						}
						data-testid="no-items"
					>
						<div className={styles['no-data-to-show']}>
							{NO_ITEMS_TO_DISPLAY}
						</div>
					</Stack>
				)}
				{openAddModal && (
					<div>
						<AddCoveredProduct
							onCloseModal={closeEditModal}
							onAdd={props?.onAdd}
							entityId={entityId}
							entityType={entityType}
						/>
					</div>
				)}
			</CardContent>
			<ModalEle
				open={showDeletionModal}
				sxBoxProps={{ width: '30%', height: '30%', margin: 'top' }}
			>
				<Grid container alignItems="center">
					<Grid item xs={11} lg={11} sm={11} md={11}>
						<div>
							<span
								className={styles['product-delete-modal-title']}
								data-testid="title-text"
							>
								Remove Covered Product
							</span>
						</div>
					</Grid>

					<Grid
						item
						xs={1}
						lg={1}
						sm={1}
						md={1}
						className={styles['add-control-test-step-title-close']}
					>
						<div className={styles['add-control-close-icon']}>
							<CloseIcon
								data-testid="on-close"
								onClick={handleModalCancelClick}
								fill={drawerCloseIconColor}
							/>
						</div>
					</Grid>
					<Grid item className={styles['product-delete-modal-sub-title']}>
						<Typography>{deletionMessage}</Typography>
					</Grid>
					<Grid item xs={12}>
						<Alert
							severity="warning"
							sx={{
								background: statusWarningColor,
								color: 'black',
								'& .MuiAlert-icon': {
									color: statusRed,
								},
							}}
						>
							This action cannot be undone.
						</Alert>
					</Grid>
					<Grid item xs={12} className={styles['product-button-align']}>
						<Grid container justifyContent="flex-end" spacing={2}>
							<Grid item>
								<ButtonComp
									btnType="secondary"
									clickHandler={handleModalCancelClick}
								>
									Cancel
								</ButtonComp>
							</Grid>
							<Grid item>
								<ButtonComp
									btnType="primary"
									clickHandler={handleRemoveItem}
									data-testid="remove-button"
								>
									Remove
								</ButtonComp>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</ModalEle>
		</Card>
	);
};

export default CoveredComp;
